import { trackDownloadInstructions } from 'actions';
import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Instructions } from 'components/InstructionUI/InstructionUI';
import { Heading } from 'components/ui/Heading/Heading';
import { AUTH_LETTER, InstructionsType, PAUSED_ERROR } from 'constants/index';
import { useModalContext } from 'hooks';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import {
  getInstructionsType,
  getLinkById,
  getPaymentId,
  getPaymentInstructionsWithoutExcluded,
  getPaymentPrice,
  getPaymentTaxes,
  getPayNowQRCode,
  getRecipientName,
} from 'selectors';
import { getCreatePaymentError } from 'selectors/ui/ui';
import { createMarkup, useTranslations } from 'utils';
import { ReactComponent as InfoIcon } from '../../../src/components/PaymentOptions/components/Option/assets/ico-info-blue.svg';
import { PausedError } from '../PausedError/PausedError';
import { MissingInstructionsError } from './components/MissingInstructionsError/MissingInstructionsError';
import { ProgressBar } from './components/ProgressBar/ProgressBar';
import downloadInstructionsIcon from './downloadInstructions.svg';

import './PaymentInstructions.scss';

export type PaymentInstructionsProps = {
  instructionsErrors?: string[];
  isOfflinePayment?: boolean;
};

export const PaymentInstructionsComponent = ({
  instructionsErrors,
  isOfflinePayment = false,
}: PaymentInstructionsProps) => {
  const dispatch = useDispatch();
  const createPaymentError = useSelector(getCreatePaymentError);
  const i18n = useTranslations();

  const id = useSelector(getPaymentId);
  const instructions = useSelector(getPaymentInstructionsWithoutExcluded);
  const payNowQRCode = useSelector(getPayNowQRCode);
  const instructionsType = useSelector(getInstructionsType);
  const downloadLink = useSelector((state: RootState) =>
    getLinkById(state, AUTH_LETTER),
  );
  const price = useSelector(getPaymentPrice);
  const taxes = useSelector(getPaymentTaxes);
  const recipientName = useSelector(getRecipientName);

  const handleTrackDownloadInstructions = () => {
    dispatch(trackDownloadInstructions() as any);
  };

  const missingInstructions = instructions.length === 0;

  if (createPaymentError === PAUSED_ERROR) {
    return <PausedError />;
  }

  if (missingInstructions) {
    return <MissingInstructionsError instructionsErrors={instructionsErrors} />;
  }

  const isPayNowPayment = instructionsType === InstructionsType.PAYNOW;
  const { Instruction, Label, Value } = Instructions;
  const amountToPay = (
    <div className="PaymentInstructions-row" data-testid="amountToPay">
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t('checkout.instructions.offline.amount.label')}
        </Label>
        <Value className="Checkout-Instruction-value fontWeight-semiBold">
          <FormattedAmount
            value={price.value}
            currency={price.currency as string}
          />{' '}
          {taxes && taxes.value > 0 && (
            <span>
              (Including taxes:{' '}
              <FormattedAmount
                value={taxes.value}
                currency={taxes.currency as string}
              />
              )
            </span>
          )}
        </Value>
      </Instruction>
    </div>
  );

  const paymentId = (
    <div className="PaymentInstructions-row" data-testid="paymentId">
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t('checkout.instructions.offline.reference.label')}
        </Label>
        <Value
          value={id || ''}
          className="Checkout-Instruction-value fontWeight-semiBold"
        />
      </Instruction>
    </div>
  );

  const _instructions = isPayNowPayment
    ? instructions.filter(instruction => instruction.name !== 'aba')
    : instructions;

  const paymentInstructions = _instructions.map(instruction => (
    <div className="PaymentInstructions-row" key={instruction.name}>
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t(instruction.label)}
        </Label>
        <Value
          value={instruction.value}
          className="Checkout-Instruction-value fontWeight-semiBold"
        />
      </Instruction>
    </div>
  ));

  const shouldHideInstructions =
    isPayNowPayment && paymentInstructions.length === 0;

  return (
    <>
      <Heading as="h2" size="large" className="fontWeight-semiBold">
        {i18n.t('checkout.instructions.offline.title')}
      </Heading>
      <p className="type-regular marginBottom-lg">
        {i18n.t('checkout.instructions.offline.description')}
      </p>
      {isOfflinePayment && <ProgressBar recipientName={recipientName || ''} />}

      {isPayNowPayment && (
        <div className="PayNowQRCode" data-testid="payNowQRCode">
          <h4>{i18n.t('checkout.instructions.offline.title.payNow')}</h4>
          <span dangerouslySetInnerHTML={createMarkup(payNowQRCode?.value)} />
          {!shouldHideInstructions && (
            <h4>
              {i18n.t('checkout.instructions.offline.title.payNowInstructions')}
            </h4>
          )}
        </div>
      )}
      {!shouldHideInstructions && (
        <div className="Checkout-PaymentInstructions-details">
          <p className="type-regular marginBottom-md PaymentInstructions-bankInstructions">
            {i18n.t('checkout.instructions.offline.bankInstructions')}
          </p>
          <hr />
          <div className="PaymentInstructions-list">
            {amountToPay}
            {paymentId}
            {paymentInstructions}
          </div>
        </div>
      )}
      {!shouldHideInstructions && downloadLink && (
        <div className="Instruction-authLetter">
          {isOfflinePayment && (
            <div className="Instruction-authLetter-info">
              <InfoIcon />
              <span>
                {i18n.t(
                  'bankTransfer.instructions.paymentDetailsStep.download.instructions.info',
                )}
              </span>
            </div>
          )}
          <div className="Instruction-authLetterLink">
            <img src={downloadInstructionsIcon} alt="" />
            <a
              href={downloadLink.href}
              target="_self"
              data-testid="authLetter"
              onClick={handleTrackDownloadInstructions}
            >
              {i18n.t('checkout.instructions.offline.download')}
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const PaymentInstructions = (props: PaymentInstructionsProps = {}) => {
  const { dispatch } = useModalContext();

  useEffect(() => {
    dispatch({ type: 'BANK_TRANSFER_INITIATED' });
  }, [dispatch]);

  return <PaymentInstructionsComponent {...props} />;
};

export { PaymentInstructions };
